import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Actividad, Grupo } from '../types';
import { GroupManagement } from './GroupManagement';
import { AddActivityForm } from './AddActivityForm';
import { PasswordModal } from './PasswordModal';
import { Edit2, Trash2, Calendar, MapPin, Users, Plus, Settings, Filter } from 'lucide-react';

interface ActivitiesViewProps {
  selectedYear: string;
  onYearChange: (year: string) => void;
  availableYears: string[];
}

export const ActivitiesView: React.FC<ActivitiesViewProps> = ({
  selectedYear,
  onYearChange,
  availableYears
}) => {
  const [actividades, setActividades] = useState<Actividad[]>([]);
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [showGroupManagement, setShowGroupManagement] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Actividad | null>(null);

  useEffect(() => {
    loadActividades();
    loadGrupos();
  }, [selectedGroup, selectedYear]);

  const loadActividades = async () => {
    let query = supabase
      .from('mapa_actividades')
      .select(`
        *,
        grupo:mapa_grupos(*)
      `)
      .order('fecha', { ascending: false });

    if (selectedGroup) {
      query = query.eq('grupo_id', selectedGroup);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error loading activities:', error);
    } else {
      const allActividades = data || [];
      setActividades(allActividades);
    }
  };

  const loadGrupos = async () => {
    const { data, error } = await supabase
      .from('mapa_grupos')
      .select('*')
      .order('nombre');

    if (error) {
      console.error('Error loading groups:', error);
    } else {
      setGrupos(data || []);
    }
  };

  const deleteActivity = async (id: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta actividad?')) {
      const { error } = await supabase
        .from('mapa_actividades')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting activity:', error);
        alert('Error al eliminar la actividad');
      } else {
        loadActividades();
      }
    }
  };

  const getFilteredActividades = () => {
    let filtered = actividades;
    
    if (selectedGroup) {
      filtered = filtered.filter(actividad => actividad.grupo_id === selectedGroup);
    }
    
    if (selectedYear) {
      filtered = filtered.filter(actividad => 
        new Date(actividad.fecha).getFullYear().toString() === selectedYear
      );
    }
    
    return filtered;
  };

  const filteredActividades = getFilteredActividades();

  const handlePasswordSuccess = () => {
    setShowPasswordModal(false);
    setShowAddForm(true);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
  };

  return (
    <div className="h-full bg-gray-50 overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Actividades</h1>
            <p className="text-gray-600 mt-1">Gestiona tus lugares visitados</p>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Nueva Actividad</span>
            </button>
            <button
              onClick={() => setShowGroupManagement(true)}
              className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Settings className="h-4 w-4" />
              <span>Gestionar Grupos</span>
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
          <div className="flex items-center space-x-2 mb-3">
            <Users className="h-4 w-4 text-gray-600" />
            <h3 className="font-medium text-gray-900">Filtro por Grupo</h3>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Todos los grupos</option>
                {grupos.map((grupo) => (
                  <option key={grupo.id} value={grupo.id}>
                    {grupo.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-sm text-gray-600">
              Mostrando: <span className="font-medium">{filteredActividades.length}</span> de <span className="font-medium">{actividades.length}</span> actividades
            </div>
          </div>
        </div>

        {/* Lista de actividades */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredActividades.map((actividad) => (
            <div
              key={actividad.id}
              className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {actividad.nombre}
                  </h3>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => setEditingActivity(actividad)}
                      className="text-gray-400 hover:text-blue-600 transition-colors"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => deleteActivity(actividad.id!)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4" />
                    <span>{actividad.latitud.toFixed(4)}, {actividad.longitud.toFixed(4)}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Calendar className="h-4 w-4" />
                    <span>{new Date(actividad.fecha).toLocaleDateString()}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <div className="h-4 w-4 rounded-full bg-gradient-to-r from-blue-400 to-red-400"></div>
                    <span>Alcance: {actividad.alcance || 50}</span>
                  </div>
                  
                  {actividad.grupo && (
                    <div className="flex items-center space-x-2 text-sm">
                      <Users className="h-4 w-4 text-gray-600" />
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: actividad.grupo.color }}
                        ></div>
                        <span className="text-gray-600">{actividad.grupo.nombre}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredActividades.length === 0 && (
          <div className="text-center py-12">
            <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay actividades
            </h3>
            <p className="text-gray-600">
              {selectedGroup || selectedYear 
                ? 'No hay actividades que coincidan con los filtros seleccionados' 
                : 'Agrega tu primera actividad en el mapa'
              }
            </p>
          </div>
        )}
      </div>

      {/* Modal de gestión de grupos */}
      {showGroupManagement && (
        <GroupManagement
          onClose={() => setShowGroupManagement(false)}
          onGroupsUpdated={loadGrupos}
        />
      )}

      {/* Modal de contraseña */}
      {showPasswordModal && (
        <PasswordModal
          onClose={handleClosePasswordModal}
          onSuccess={handlePasswordSuccess}
        />
      )}

      {/* Modal para agregar actividad */}
      {showAddForm && (
        <AddActivityForm
          onClose={() => setShowAddForm(false)}
          onActivityAdded={() => {
            loadActividades();
            setShowAddForm(false);
          }}
        />
      )}
    </div>
  );
};